import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { fetchJson } from '../../../utils/fetchJson';
import { UserContext } from '../../../contexts/UserContext';

export default function SystemCommunicationTab() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const [messages, setMessages] = useState([]);
  const [type, setType] = useState('inbox');
  const [activeTab, setActiveTab] = useState('inbox');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(false);

  const [broadcast, setBroadcast] = useState(false);
  const [recipientQuery, setRecipientQuery] = useState('');
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [toUserId, setToUserId] = useState('');

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');

  const [attachments, setAttachments] = useState([]);
  const MAX_FILES = 5;
  const MAX_SIZE = 10 * 1024 * 1024;

  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const [broadcastRole, setBroadcastRole] = useState('SchoolAdmin');
  const [recipientRole, setRecipientRole] = useState('SchoolAdmin');
  const roleParam = broadcast ? broadcastRole : (recipientRole || '');

  useEffect(() => {
    if (activeTab === 'compose') return;
    if (activeTab === 'inbox') setType('inbox');
    if (activeTab === 'sent') setType('sent');
  }, [activeTab]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('type', type);
      if (search) params.set('search', search);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (archived) params.set('archived', 'true');
      const res = await fetchJson(`/api/messages?${params.toString()}`);
      setMessages(Array.isArray(res?.messages) ? res.messages : []);
    } catch (e) {
      setError(e?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [type, archived]);

  useEffect(() => {
    let timer = null;
    const run = async () => {
      if (!recipientQuery || recipientQuery.length < 2) {
        setRecipientOptions([]);
        return;
      }
      setRecipientLoading(true);
      try {
        const data = await fetchJson(`/api/messages/recipients?query=${encodeURIComponent(recipientQuery)}&limit=10&role=${encodeURIComponent(roleParam)}`);
        setRecipientOptions(Array.isArray(data?.users) ? data.users : []);
      } catch (_) {
        setRecipientOptions([]);
      } finally {
        setRecipientLoading(false);
      }
    };
    timer = setTimeout(run, 300);
    return () => { if (timer) clearTimeout(timer); };
  }, [recipientQuery, recipientRole]);

  const authHeaderValue = useMemo(() => {
    const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt'];
    for (const k of tokenKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) return `Bearer ${String(v).replace(/^Bearer\s+/i, '').trim()}`;
    }
    return '';
  }, []);

  const canSend = useMemo(() => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes('communications');
  }, [user]);

  const onUpload = async (e) => {
    if (!canSend) return;
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) {
      setError(`Maximum ${MAX_FILES} attachments allowed`);
      return;
    }
    for (const f of files) {
      if (f.size > MAX_SIZE) {
        setError(`File ${f.name} exceeds 10MB limit`);
        return;
      }
    }
    try {
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append('file', f);
        const data = await fetchJson('/api/messages/attachment', {
          method: 'POST',
          ...(authHeaderValue ? { headers: { Authorization: authHeaderValue } } : {}),
          body: form
        });
        uploaded.push(data);
      }
      setAttachments(prev => [...prev, ...uploaded]);
      e.target.value = '';
    } catch (err) {
      setError(err?.message || 'Upload failed');
    }
  };

  const send = async () => {
    if (!canSend) return;
    setError('');
    setInfo('');
    try {
      if (!content) throw new Error(t('sysCommunicationTab.messageRequired', 'Message is required'));
      if (broadcast) {
        await fetchJson('/api/messages/broadcast', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: roleParam, subject: subject || null, content, attachments: attachments.map(a => a.url) })
        });
      } else {
        if (!toUserId) throw new Error(t('sysCommunicationTab.selectRecipient', 'Select a recipient'));
        await fetchJson('/api/messages', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ toUserId: Number(toUserId), subject: subject || null, content, attachments: attachments.map(a => a.url) })
        });
      }
      setContent('');
      setSubject('');
      setToUserId('');
      setRecipientQuery('');
      setRecipientOptions([]);
      setAttachments([]);
      setInfo(t('sysCommunicationTab.messageSent', 'Message sent'));
      await load();
    } catch (e) {
      setError(e?.message || t('sysCommunicationTab.failedSend', 'Failed to send message'));
    }
  };

  const markRead = async (id) => {
    if (!canSend) return;
    try {
      await fetchJson(`/api/messages/${id}/read`, { method: 'PATCH' });
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('sysCommunicationTab.failedMarkRead', 'Failed to mark read') });
    }
  };

  const toggleArchive = async (id, value) => {
    if (!canSend) return;
    try {
      await fetchJson(`/api/messages/${id}/archive`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ archived: !!value }) });
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('sysCommunicationTab.failedArchive', 'Failed to update archive') });
    }
  };

  const remove = async (id) => {
    if (!canSend) return;
    try {
      await fetchJson(`/api/messages/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setSnack({ open: true, severity: 'error', message: e?.message || t('sysCommunicationTab.failedDelete', 'Failed to delete message') });
    }
  };

  const resetComposer = () => {
    setContent('');
    setSubject('');
    setToUserId('');
    setRecipientQuery('');
    setRecipientOptions([]);
    setAttachments([]);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t('sysCommunicationTab.title', 'Communication')}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="compose" label={t('sysCommunicationTab.compose', 'Compose')} />
          <Tab value="inbox" label={t('sysCommunicationTab.inbox', 'Inbox')} />
          <Tab value="sent" label={t('sysCommunicationTab.sent', 'Sent')} />
        </Tabs>

        {activeTab === 'compose' && (
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
              <FormControlLabel control={<Switch checked={broadcast} onChange={(_, v) => setBroadcast(v)} />} label={t('sysCommunicationTab.broadcastMode', 'Broadcast Mode')} />

              {broadcast && (
                <TextField
                  select
                  size="small"
                  label={t('sysCommunicationTab.targetRole', 'Target Role')}
                  value={broadcastRole}
                  onChange={(e) => setBroadcastRole(e.target.value)}
                  sx={{ minWidth: 180 }}
                  SelectProps={{ native: true }}
                  disabled={!canSend}
                >
                  <option value="SchoolAdmin">{t('sysCommunicationTab.roles.schoolAdmin', 'School Admins')}</option>
                  <option value="SystemAdmin">{t('sysCommunicationTab.roles.systemAdmin', 'System Admins')}</option>
                </TextField>
              )}

              {!broadcast && (
                <>
                  <TextField
                    select
                    size="small"
                    label={t('sysCommunicationTab.filterByRole', 'Filter by Role')}
                    value={recipientRole}
                    onChange={(e) => { setRecipientRole(e.target.value); setRecipientQuery(''); setRecipientOptions([]); }}
                    sx={{ minWidth: 150 }}
                    SelectProps={{ native: true }}
                    disabled={!canSend}
                  >
                    <option value="SchoolAdmin">{t('sysCommunicationTab.roles.schoolAdmin', 'School Admins')}</option>
                    <option value="SystemAdmin">{t('sysCommunicationTab.roles.systemAdmin', 'System Admins')}</option>
                  </TextField>
                  <Autocomplete
                    options={recipientOptions}
                    loading={recipientLoading}
                    getOptionLabel={(opt) => `${opt.name}${opt.email ? ' • ' + opt.email : ''}${opt.role ? ' (' + opt.role + ')' : ''}`}
                    onInputChange={(_, v) => setRecipientQuery(v)}
                    onChange={(_, v) => setToUserId(v?.id ? String(v.id) : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('sysCommunicationTab.searchUser', 'Search User')}
                        size="small"
                        sx={{ minWidth: 320 }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {recipientLoading ? <CircularProgress color="inherit" size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                  />
                </>
              )}
            </Stack>

            <Stack spacing={2} sx={{ mb: 2, width: '100%', maxWidth: { xs: '100%', md: 420 } }}>
              <TextField
                label={t('sysCommunicationTab.subject', 'Subject')}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                size="small"
                fullWidth
                disabled={!canSend}
              />
              <TextField
                label={t('sysCommunicationTab.message', 'Message')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                size="small"
                fullWidth
                multiline
                minRows={6}
                disabled={!canSend}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Button variant="outlined" component="label" disabled={!canSend}>
                  {t('sysCommunicationTab.attach', 'Attach')}
                  <input hidden multiple type="file" onChange={onUpload} />
                </Button>

                <Button variant="contained" onClick={send} disabled={!canSend}>{t('sysCommunicationTab.send', 'Send')}</Button>
                <Button variant="text" onClick={resetComposer} disabled={!canSend}>{t('sysCommunicationTab.clear', 'Clear')}</Button>
              </Stack>
            </Stack>

            {attachments.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', maxWidth: { xs: '100%', md: 420 } }}>
                {attachments.map((a, idx) => (
                  <Chip
                    key={`${a.url}-${idx}`}
                    label={a.filename || a.url}
                    onDelete={canSend ? () => setAttachments(prev => prev.filter((_, i) => i !== idx)) : undefined}
                    clickable
                    component="a"
                    href={a.url}
                    target="_blank"
                    rel="noopener"
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {activeTab !== 'compose' && (
          <Box sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
              <TextField label={t('sysCommunicationTab.search', 'Search')} value={search} onChange={(e) => setSearch(e.target.value)} size="small" />
              <TextField label={t('sysCommunicationTab.from', 'From')} type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
              <TextField label={t('sysCommunicationTab.to', 'To')} type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} size="small" InputLabelProps={{ shrink: true }} />

              <FormControlLabel control={<Switch checked={archived} onChange={(_, v) => setArchived(v)} />} label={t('sysCommunicationTab.showArchived', 'Show archived')} />

              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>{t('sysCommunicationTab.refresh', 'Refresh')}</Button>
            </Stack>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {loading ? (
                <Box sx={{ p: 3 }}><CircularProgress size={24} /></Box>
              ) : (
                <Box>
                  {messages.length === 0 ? (
                    <Box sx={{ p: 3, color: 'text.secondary' }}>{t('sysCommunicationTab.noMessages', 'No messages')}</Box>
                  ) : (
                    messages.map((m, idx) => (
                      <Box key={m.id} sx={{ p: 2, borderTop: idx === 0 ? 'none' : '1px solid', borderColor: 'divider' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                          <Box>
                            <Typography fontWeight={600}>{m.subject || t('sysCommunicationTab.noSubject', '(No subject)')}</Typography>
                            <Typography variant="body2" color="text.secondary">{new Date(m.createdAt).toLocaleString()}</Typography>
                            <Typography sx={{ mt: 1 }}>{m.content}</Typography>
                            {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {m.attachments.map((u, i) => (
                                  <Chip key={`${m.id}-att-${i}`} label={u} component="a" href={u} target="_blank" clickable />
                                ))}
                              </Box>
                            )}
                          </Box>

                          <Stack direction="row" spacing={1} alignItems="center">
                            {type === 'inbox' && !m.readAt && (
                              <Button size="small" startIcon={<MarkEmailReadIcon />} onClick={() => markRead(m.id)} disabled={!canSend}>
                                {t('sysCommunicationTab.markRead', 'Mark read')}
                              </Button>
                            )}
                            {m.archived ? (
                              <Button size="small" startIcon={<UnarchiveIcon />} onClick={() => toggleArchive(m.id, false)} disabled={!canSend}>
                                {t('sysCommunicationTab.unarchive', 'Unarchive')}
                              </Button>
                            ) : (
                              <Button size="small" startIcon={<ArchiveIcon />} onClick={() => toggleArchive(m.id, true)} disabled={!canSend}>
                                {t('sysCommunicationTab.archive', 'Archive')}
                              </Button>
                            )}
                            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => remove(m.id)} disabled={!canSend}>
                              {t('sysCommunicationTab.delete', 'Delete')}
                            </Button>
                          </Stack>
                        </Stack>
                      </Box>
                    ))
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4500}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
